// Wind River 제품별 BlockNote 초기 콘텐츠
// scraped from windriver.com/products (2026-06-22)

const t = (text, styles = {}) => ({ type: 'text', text, styles });

const heading = (text, level = 2) => ({
  type: 'heading',
  props: { level },
  content: [t(text)],
});

const para = (text) => ({
  type: 'paragraph',
  content: [t(text)],
});

const capCards = (cards) => ({
  type: 'capCards',
  props: { cards: JSON.stringify(cards) },
});

const certBadge = (text) => ({
  type: 'certBadge',
  props: { text },
});

const ctaButtons = (primary, primaryUrl, secondary, secondaryUrl) => ({
  type: 'ctaButtons',
  props: { primary, primaryUrl, secondary, secondaryUrl },
});

export const WR_BN_CONTENT = {
  vxworks: [
    para("Build high-performance, safety-certifiable embedded systems software, where 99.9999% reliability won't do. Trusted by NASA, Lockheed Martin, Collins Aerospace, and major automotive OEMs, VxWorks powers systems that demand absolute reliability with microsecond-level response times and 15-year lifecycle support."),
    heading('Key Capabilities'),
    capCards([
      { icon: '⏱', title: 'Designed for Hard Real Time', body: 'Single-Nanosecond Determinism' },
      { icon: '🛡', title: 'Safety-Certifiable, 600+ Programs Certified', body: 'DO-178C / EUROCAE ED-12C, IEC 61508, IEC 62304, ISO 26262' },
      { icon: '🤖', title: 'Edge AI-Ready', body: 'Rapidly implement AI/ML applications at the intelligent edge' },
      { icon: '🌐', title: 'Full TSN Software Stack', body: 'Achieve deterministic networking with a full TSN stack' },
    ]),
    heading('Why Wind River?'),
    heading('The Edge of Performance', 3),
    para('Low latency and jitter for determinism and reliability enable advanced applications development, on the latest hardware, for mission-critical systems that must not fail.'),
    heading('Cost-effective Safety Certification', 3),
    para('Pre-certified with optional evidence packages for accelerating project certification, all backed by our industry-leading expertise.'),
    heading('Cloud-enabled Development', 3),
    para('Deliver high-quality, compliant software for safety-critical systems, on time and on budget, encompassing secure coding, simulation, automated testing, and lifecycle management.'),
    certBadge('DO-178C / IEC 61508 / ISO 26262 / FACE™'),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/vxworks'),
  ],

  linux: [
    para("Wind River Linux is the industry's most advanced embedded Linux development platform, purpose-built for intelligent edge devices. As a Yocto Project founder, platinum member, and top contributing organization, Wind River delivers commercial-grade Linux with long-term support, proactive CVE remediation, and full compliance documentation."),
    para("Build and deploy robust, secure Linux-based edge devices without the risk and effort of roll-your-own approaches. Wind River handles security patches, SBOM generation, and export compliance artifacts so you can focus on innovation."),
    heading('Key Capabilities'),
    capCards([
      { icon: '🔒', title: 'Long-Term Support & Maintenance', body: '10+ years CVE monitoring, daily testing, BSPs' },
      { icon: '🖥', title: 'Hardware-Independent BSP Support', body: 'Pre-validated BSPs for AMD, Intel, NXP, Nvidia' },
      { icon: '🛡', title: 'Security & Compliance Built In', body: 'Proactive CVE mitigation, SBOM, export artifacts, SDL' },
      { icon: '🏆', title: 'Yocto Project Leadership', body: 'Founder, Platinum member and Top contributing organization' },
    ]),
    heading('Why Wind River?'),
    heading('Community innovation with commercial assurance', 3),
    para('Wind River Linux subscription will ensure your code base is up to date, track and fix defects, apply security patches, customize your runtime to adhere to strict market specifications and certifications, comply with government regulations and mandates and facilitate your Intellectual Property (IP) and Export compliance.'),
    para('Wind River Linux serves as a trusted, production-grade platform for mission-critical applications, far surpassing the support levels and lifecycle guarantees of community-maintained FOSS distributions.'),
    certBadge('Yocto Project Platinum Member · 10+ Year LTS'),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/linux'),
  ],

  helix: [
    para('Consolidate multiple systems onto one embedded platform, streamlining safety certification and reducing project risk, with inherent cost savings in embedded systems development.'),
    para('Run VxWorks RTOS, Linux, Android and other operating systems, safely isolated from each other via high-performance virtualization, with Helix Platform.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '🔀', title: 'Certifiable Mixed-Criticality', body: 'Safety-critical and non-critical on one multi-core system.' },
      { icon: '♻️', title: 'Legacy Software Re-use', body: 'Port existing, proven applications to new platforms.' },
      { icon: '✅', title: 'DO-178C / ED-12C, ISO 26262, IEC 61508', body: 'Pre-certified evidence accelerates compliance.' },
      { icon: '🔗', title: 'Pre-Integrated with VxWorks & Linux', body: 'Rapid turn-up with single vendor solution.' },
    ]),
    heading('Why Wind River?'),
    heading('Reduce Space, Weight, Power and Cost (SWaP-C)', 3),
    para('Consolidate software functions on a single platform, reducing SWaP-C. Save on development and, ultimately, the platform\'s operational costs.'),
    heading('Ease the Path to Safety Certification', 3),
    para('Achieve DO-178C DAL A, ISO 26262 ASIL D, and IEC 61508 SIL 3 faster. Pre-certified COTS evidence enables a focus on revenue-generating applications development.'),
    heading('De-Risk Multi-System Consolidation', 3),
    para('Complex embedded projects carry significant risks. Helix Platform\'s proven technology, pre-integrated hardware support, and expert services reduce project complexity.'),
    certBadge('DO-178C DAL A / ISO 26262 ASIL D / IEC 61508 SIL 3'),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/helix'),
  ],

  'cloud-platform': [
    para('Wind River Cloud Platform is a private cloud platform for organizations requiring secure, high-performance infrastructure with mission-critical reliability. Built on StarlingX open source technology, Cloud Platform manages both containerized and virtualized applications from a single console across distributed deployments.'),
    para('Deploy in data centers or edge sites with carrier-grade availability, zero-touch provisioning, and full data sovereignty. Trusted by Verizon for 5G vRAN infrastructure and chosen by Boost Mobile as a VMware replacement, Cloud Platform scales from single nodes to thousands of distributed sites.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '📦', title: 'Industry-Leading Small Footprint', body: 'Optimized for resource-constrained edge sites' },
      { icon: '🔄', title: 'Legacy Workload Transition', body: 'Unified virtual machine and container management' },
      { icon: '🔐', title: 'Cloud with Full Control and Security', body: 'High performance with zero-trust security model' },
      { icon: '🤖', title: 'Distributed Artificial Intelligence', body: 'Designed for mission-critical AI workloads' },
    ]),
    heading('Powered by open-source innovation', 3),
    para('Powered by open-source innovation, Cloud Platform unifies and automates infrastructure management—delivering speed, efficiency, reliability, and cloud sovereignty from data center to edge to OT environments.'),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/cloud-platform'),
  ],

  'elxr-pro': [
    para('eLxr Pro™ provides Debian-based enterprise Linux with support across edge, cloud, and data centers – cutting costs, avoiding lock-in, and ensuring consistent, adaptable deployments.'),
    para('Organizations transitioning from proprietary platforms gain predictable costs, long-term support, and a true open source community experience rather than vendor lock-in.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '📈', title: 'Scalable, Efficient Edge-to-Cloud Ops', body: 'Scalable deployments with lower ops and costs' },
      { icon: '🌐', title: 'Open, Portable, Ecosystem-Ready', body: 'Enterprise Linux extending ecosystem and lifecycle' },
      { icon: '⚡', title: 'Enterprise High-Performance', body: 'Predictable, low-latency performance for edge AI' },
      { icon: '🛡', title: 'Secure Architecture for Critical Systems', body: 'Built-in security and updates for compliance' },
    ]),
    heading('Why eLxr Pro?', 3),
    para('eLxr Pro is an enterprise Linux distribution optimized for edge and cloud deployments. Built on Debian with commercial-grade support, eLxr Pro provides a high-performance, hardened kernel with the security and compliance capabilities enterprises require.'),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/elxr-pro'),
  ],

  workspace: [
    para('Workspace standardizes embedded software development with secure, fully configured, cloud-based development environments on demand. Onboard new team members in minutes instead of days with preconfigured workspaces.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '☁️', title: 'Preconfigured Environments', body: 'Instant dev environments with all tools pre-installed' },
      { icon: '🔒', title: 'Secure Collaboration', body: 'Role-based access and audit trails for team workflows' },
      { icon: '💻', title: 'IDE Flexibility', body: 'VS Code-compatible in cloud or on-premises deployment' },
      { icon: '🔗', title: 'CI/CD Integration', body: 'Seamless connection to popular DevOps toolchains' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/studio/workspace'),
  ],

  'diab-compiler': [
    para('TÜV SÜD-certified optimizing C/C++ compiler for embedded systems. Generates high-quality code that minimizes memory usage and maximizes mission-critical performance.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '✅', title: 'Safety Certifiable', body: 'TÜV Certified for DO-178C, IEC 61508, and ISO 26262' },
      { icon: '⚡', title: 'Optimized Code', body: 'Superior performance on Arm, PowerPC, RISC-V, and x86' },
      { icon: '📋', title: 'Standards Compliance', body: 'Full C11/C++17 standards support with MISRA checking' },
      { icon: '🔄', title: 'Long Lifecycle', body: 'Compiler stability across multi-decade program lifetimes' },
    ]),
    certBadge('TÜV Certified · DO-178C · IEC 61508 · ISO 26262'),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/diab-compiler'),
  ],

  'pipeline-manager': [
    para('Automate continuous build, test, and deployment with reusable pipeline components. Customers have achieved 30% faster pipeline execution after switching from Jenkins.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '🔄', title: 'Automated Pipelines', body: 'Build, test, and release automation for embedded SW teams' },
      { icon: '🔗', title: 'Tool Integration', body: 'Connects with Jira, Jenkins, GitLab, and more' },
      { icon: '📋', title: 'Compliance Traceability', body: 'Artifact traceability for DO-178C and IEC 62443 audits' },
      { icon: '🚀', title: 'Scalable Execution', body: 'Distributed agent pools for parallel pipeline execution' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/studio/pipeline-manager'),
  ],

  'test-automation': [
    para('Optimize embedded software verification at cloud scale with modular, plugin-based, automation architecture. Save millions of dollars and get to market sooner with increased automation.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '☁️', title: 'Cloud-Scale Testing', body: 'Massively parallel test execution in the cloud' },
      { icon: '🖥', title: 'Hardware-in-the-Loop', body: 'Connect real hardware targets through Virtual Lab integration' },
      { icon: '📊', title: 'Coverage Analysis', body: 'Code and requirements coverage reporting for certification' },
      { icon: '🔁', title: 'Regression Automation', body: 'Continuous regression suites with automatic failure triage' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/studio/test-automation-framework'),
  ],

  'virtual-lab': [
    para('Manage and share simulated and physical hardware resources in the cloud for optimal use of testing and debugging resources. Provide everyone on your team access to test harnesses on-demand.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '🌐', title: 'Remote Target Access', body: 'Secure browser-based access to physical and simulated HW' },
      { icon: '📅', title: 'Resource Scheduling', body: 'Queue and share scarce hardware resources across teams' },
      { icon: '🔬', title: 'Simics Integration', body: 'Seamless simulation when physical targets are unavailable' },
      { icon: '⚙️', title: 'Lab Automation', body: 'Script-driven board power, flash, and reset operations' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/studio/virtual-lab'),
  ],

  simics: [
    para("Intel® Simics® decouples software developers' work from hardware availability, enabling true DevSecOps practices to deliver better software faster. Test embedded software before hardware exists with full-system simulation. Delivers up to 45% reduction in CapEx and OpEx while enabling parallel development."),
    heading('Key Capabilities'),
    capCards([
      { icon: '🖥', title: 'Full-System Simulation', body: 'Simulate complete SoCs, boards, and multi-node networks' },
      { icon: '⏺', title: 'Deterministic Replay', body: 'Record and replay exact execution for reliable debugging' },
      { icon: '🔬', title: 'Pre-Silicon Development', body: 'Start software development before hardware is available' },
      { icon: '🛡', title: 'Security Testing', body: 'Inject faults and test security responses safely in simulation' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/simics'),
  ],

  'ota-updates': [
    para('Deploy secure software updates to embedded devices and IoT endpoints remotely. Enable rapid security updates and deploy new features to fielded devices.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '🔏', title: 'Secure Delivery', body: 'Signed and encrypted update packages with rollback protection' },
      { icon: '📡', title: 'Fleet Management', body: 'Manage updates across thousands of distributed devices' },
      { icon: '🎯', title: 'Staged Rollouts', body: 'Canary and phased rollout strategies to manage risk' },
      { icon: '📋', title: 'Compliance Auditing', body: 'Full audit trail for regulatory and certification requirements' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/studio/operate/over-the-air-updates'),
  ],

  conductor: [
    para('Zero-touch edge orchestration for multi-cloud deployments. Scale from a handful of nodes to thousands while cutting human intervention by 90%.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '🎛', title: 'Unified Control Plane', body: 'Centralized management for edge, cloud, and hybrid deployments' },
      { icon: '📦', title: 'GitOps Workflows', body: 'Declarative configuration management with Git-based workflows' },
      { icon: '🔀', title: 'Multi-Cluster Support', body: 'Orchestrate workloads across many Kubernetes clusters' },
      { icon: '📜', title: 'Policy Enforcement', body: 'Automated compliance policies across the entire fleet' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/conductor'),
  ],

  'digital-feedback-loop': [
    para('Collect real-time analytics from edge devices to support data-driven decisions. Deploy data gathering over the air and configure automated anomaly alerts.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '📡', title: 'Runtime Telemetry', body: 'Collect logs, metrics, and traces from deployed devices' },
      { icon: '🔍', title: 'Anomaly Detection', body: 'AI-driven detection of performance and reliability issues' },
      { icon: '🔄', title: 'Closed-Loop Feedback', body: 'Connect field data directly to development and test pipelines' },
      { icon: '✅', title: 'Compliance Monitoring', body: 'Continuous compliance checks against security baselines' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/studio/operate/digital-feedback-loop'),
  ],

  analytics: [
    para('Full-stack monitoring for distributed cloud infrastructure. Collect, analyze, and visualize behavioral data to detect anomalies and prevent problems proactively.'),
    heading('Key Capabilities'),
    capCards([
      { icon: '📊', title: 'Real-Time Dashboards', body: 'Live visibility into cloud infrastructure performance' },
      { icon: '🔔', title: 'Anomaly Detection', body: 'ML-powered alerts before issues impact availability' },
      { icon: '🗄', title: 'Data Aggregation & Enrichment', body: 'Augment structured and unstructured data with context' },
      { icon: '🔄', title: 'Data Lifecycle Management', body: 'Replication indexing ensures redundancy and fast search' },
    ]),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/analytics'),
  ],

  zephyr: [
    para("Innovate faster, stay ahead of what's next. Wind River is your Zephyr Project partner for real-time embedded use cases, delivering commercial-grade support, security hardening, and extended lifecycle maintenance for the Zephyr RTOS ecosystem."),
    heading('Key Capabilities'),
    capCards([
      { icon: '🛡', title: 'Commercial Support', body: 'Professional-grade support for the Zephyr RTOS ecosystem' },
      { icon: '🔒', title: 'Security Hardening', body: 'Proactive CVE monitoring and patch delivery for Zephyr' },
      { icon: '📅', title: 'Extended Lifecycle', body: 'Long-term maintenance beyond community release cadence' },
      { icon: '👷', title: 'Expert Engineering', body: 'Wind River engineers contributing to and supporting Zephyr' },
    ]),
    heading('Why Wind River for Zephyr?'),
    heading('Community Leadership', 3),
    para('Wind River is a leading contributor to the Zephyr Project, bringing deep embedded expertise and commercial-grade rigor to the open-source ecosystem.'),
    heading('Enterprise-Grade Security', 3),
    para('Stay ahead of vulnerabilities with Wind River\'s proactive CVE monitoring, patch qualification, and security hardening tailored for Zephyr deployments.'),
    heading('Long-Term Stability', 3),
    para('Extend the support lifetime of your Zephyr-based products beyond community cadence, ensuring continuity for multi-year embedded programs.'),
    ctaButtons('도입 문의하기', '/contact', 'Wind River 공식 페이지 →', 'https://www.windriver.com/products/zephyr'),
  ],
};

// WR_DEFAULTS에서 id로 초기 콘텐츠 찾기
export function getInitialContent(productId) {
  return WR_BN_CONTENT[productId] || null;
}
